import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Button,
  Paper,
  Container,
  Snackbar,
  Alert as MuiAlert,
  IconButton,
  Box,
  Grid,
} from '@mui/material';
import {
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  ArrowBack as ArrowBackIcon, // Import ArrowBackIcon
} from '@mui/icons-material';
import { fetchProfilesByDescription } from '../../redux/profilesSlice';
import { fetchCurrentUser } from '../../redux/usersSlice';
import { saveUserConversation } from '../../redux/conversationsSlice';
import ProfileCard from './ProfileCard';
import ProfileDetails from './ProfileDetails'; // Import ProfileDetails
import CustomLoader from "../Common/CustomLoader";
import './styles/ChatResults.css';
import './styles/ProfileList.css';

const ChatResults = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { chatProfiles, loading, error, message, apiDescription } = useSelector((state) => state.profiles);

  // Use API's returned description if available, otherwise fallback to input description
  const description = apiDescription || searchParams.get('desc') || '';

  // Group profiles by mon_profile
  const vosProfils = chatProfiles.filter(p => p.mon_profile);
  const autresProfils = chatProfiles.filter(p => !p.mon_profile);

  // Pagination for autresProfils only
  const profilesPerPage = 6;
  const [page, setPage] = useState(1);
  const indexOfLastProfile = page * profilesPerPage;
  const indexOfFirstProfile = indexOfLastProfile - profilesPerPage;
  const currentAutresProfils = autresProfils.slice(indexOfFirstProfile, indexOfLastProfile);
  const totalPages = Math.ceil(autresProfils.length / profilesPerPage);

  // Selected profile state
  const [selectedProfile, setSelectedProfile] = useState(null);

  // Add ref for scroll-to-top
  const containerRef = React.useRef(null);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Reset to page 1 when description changes
  useEffect(() => {
    setPage(1);
  }, [description]);

  useEffect(() => {
    const fetchUserAndProfiles = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/interdit');
        return;
      }

      const userAction = await dispatch(fetchCurrentUser());
      const user = userAction.payload;

      if (!user || user.roles?.includes('ROLE_USER')) {
        navigate('/interdit');
        return;
      }

      if (description.trim()) {
        dispatch(
          fetchProfilesByDescription({
            description,
            numberOfProfiles: 10,
            page: 1,
            limit: 10,
          })
        );
      }
    };

    fetchUserAndProfiles();
  }, [dispatch, description, navigate]);

  const handleSaveConversation = async () => {
    const conversationData = {
      message: description,
      result: { data: chatProfiles.slice(0, 10) }, // Save only the first 10 profiles
    };
    try {
      await dispatch(saveUserConversation(conversationData)).unwrap();
      setSnackbarMessage('Sauvegarde avec succès !');
      setSnackbarSeverity('success');
    } catch (error) {
      setSnackbarMessage('Échec de la sauvegarde.');
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Scroll to top helper
  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        background: "linear-gradient(135deg, #ffffff, #d1d9e6)", // Updated gradient
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "2rem",
      }}
    >
      <Container
        sx={{
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          borderRadius: "8px",
          boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
          padding: "2rem",
          width: "100%",
          maxWidth: "1200px",
        }}
      >
        {selectedProfile ? (
          <>
            <IconButton
              onClick={() => setSelectedProfile(null)} // Return to profile results
              sx={{
                color: '#2F4E8B',
                marginBottom: '1rem',
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <ProfileDetails
              profile={selectedProfile}
              onUpdated={() => setSelectedProfile(null)} // Reset selected profile after update
            />
          </>
        ) : (
          <Grid container spacing={4}>
            {/* Right side: main content */}
            <Grid item xs={12}>
              <Paper
                elevation={3}
                sx={{
                  padding: '1.5rem',
                  backgroundColor: '#f5f5f5',
                  marginBottom: '2rem',
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ textAlign: 'center', color: '#2F4E8B', fontWeight: 'bold' }} // Updated color
                >
                  Mots clés de votre recherche :
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ textAlign: 'center', fontSize: '1.2rem', color: '#555' }}
                >
                  {description}
                </Typography>
              </Paper>

              <Button
                onClick={handleSaveConversation}
                variant="contained"
                sx={{
                  backgroundColor: '#2F4E8B', // Updated color
                  color: '#fff',
                  marginBottom: '2rem',
                  display: 'block',
                  mx: 'auto',
                  fontWeight: 'bold',
                  '&:hover': { backgroundColor: '#4a6bad' }, // Hover effect
                }}
                disabled={loading || currentAutresProfils.length === 0}
              >
                Sauvegarder la recherche
              </Button>

              {loading && (
                <div className="loading-container">
                  <CustomLoader />
                  <Typography
                    variant="body1"
                    style={{ marginTop: '1rem', fontWeight: 'bold', fontSize: '1.2rem' }}
                  >
                    Patientez un peu s'il vous plaît, Ça peut durer quelques secondes...
                  </Typography>
                </div>
              )}

              {!loading && error && (
                <Typography variant="body1" color="error" style={{ marginTop: '1rem' }}>
                  {error}
                </Typography>
              )}

              {!loading && !error && (vosProfils.length > 0 || autresProfils.length > 0) && (
                <>
                  {/* Vos profils */}
                  {vosProfils.length > 0 && (
                    <>
                      <Typography
                        variant="h6"
                        sx={{ mt: 2, mb: 1, color: '#2F4E8B', fontWeight: 'bold', textAlign: 'left' }}
                      >
                        Vos profils
                      </Typography>
                      <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
                        {vosProfils.map((profile) => (
                          <Grid item xs={12} sm={6} md={4} key={profile.id}>
                            <ProfileCard
                              profile={profile}
                              onClick={() => setSelectedProfile(profile)}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  )}

                  {/* Autres profils */}
                  {autresProfils.length > 0 && (
                    <>
                      <Typography
                        variant="h6"
                        sx={{ mt: 4, mb: 1, color: '#2F4E8B', fontWeight: 'bold', textAlign: 'left' }}
                      >
                        Autres profils
                      </Typography>
                      <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
                        {currentAutresProfils.map((profile) => (
                          <Grid item xs={12} sm={6} md={4} key={profile.id}>
                            <ProfileCard
                              profile={profile}
                              onClick={() => setSelectedProfile(profile)}
                            />
                          </Grid>
                        ))}
                      </Grid>
                      {totalPages > 1 && (
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          mt={4}
                          gap={2}
                        >
                          <IconButton
                            onClick={() => {
                              setPage(page - 1);
                              setTimeout(scrollToTop, 0);
                            }}
                            disabled={page === 1}
                            sx={{ color: '#2F4E8B' }}
                          >
                            <ArrowBackIosIcon />
                          </IconButton>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              backgroundColor: '#2F4E8B',
                              color: '#fff',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              fontWeight: 'bold',
                            }}
                          >
                            {page}
                          </Box>
                          <IconButton
                            onClick={() => {
                              setPage(page + 1);
                              setTimeout(scrollToTop, 0);
                            }}
                            disabled={page === totalPages}
                            sx={{ color: '#2F4E8B' }}
                          >
                            <ArrowForwardIosIcon />
                          </IconButton>
                        </Box>
                      )}
                    </>
                  )}
                </>
              )}

              {!loading && message && (
                <Typography variant="body1" color="textSecondary" style={{ marginTop: '1rem' }}>
                  {message}
                </Typography>
              )}
            </Grid>
          </Grid>
        )}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <MuiAlert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ mb: 2 }}
          >
            {snackbarMessage}
          </MuiAlert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default ChatResults;
